import { createListItem, deleteListItem, getListItems, updateListItem } from '../graph/lists';
import { LISTS, READ_CACHE_MS } from './listNames';
import { equalsIgnoreCase } from './scope';
import type { Store } from './types';
import { executeListOps, type ListOp } from '../graph/listOps';

/**
 * Store owners (owner ruling 2026-10-28: we manage some stores for third-
 * party owners and want reports by owner). One row per owner — a shared
 * ownership like "Arcland/ ASB" is its own owner. A store points at its
 * owner by item id (Stores.OwnerId). Admin manages them (Admin → Owners);
 * staff read names and Active for the Owner filters; Notes stay Admin-only
 * (api/src/policy.ts); store teams read none (they never see owners).
 */

interface OwnerFields {
  Title: string;
  Active: boolean;
  OwnerNotes: string;
}

export interface Owner {
  id: string;
  name: string;
  /** Retired owners stay (stores may still point at them) but leave the pickers. Missing = active (not an access flag). */
  active: boolean;
  /** Admin-only notes (contract, contact person…); '' for everyone else. */
  notes: string;
}

export const OWNER_NAME_MAX = 120;
export const OWNER_NOTES_MAX = 2000;

function toOwner(id: string, f: Partial<OwnerFields>): Owner {
  return { id, name: (f.Title ?? '').trim(), active: f.Active !== false, notes: f.OwnerNotes ?? '' };
}

/** Every owner, by name (a small list — sorted here, not by Graph). */
export async function getOwners(): Promise<Owner[]> {
  const items = await getListItems<OwnerFields>(LISTS.owners, { cacheMs: READ_CACHE_MS });
  return items.map((i) => toOwner(i.id, i.fields)).sort(byOwnerName);
}

export function byOwnerName(a: Owner, b: Owner): number {
  return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' });
}

/** Why this name can't be saved, or null. Names are unique (trimmed, case-insensitive). */
export function ownerNameProblem(name: string, owners: readonly Owner[], selfId: string | null): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Enter the owner’s name.';
  if (trimmed.length > OWNER_NAME_MAX) return `Keep the name to ${OWNER_NAME_MAX} characters or fewer.`;
  if (owners.some((o) => o.id !== selfId && equalsIgnoreCase(o.name, trimmed))) return `“${trimmed}” is already an owner.`;
  return null;
}

export async function createOwner(owner: Omit<Owner, 'id'>): Promise<Owner> {
  const created = await createListItem<OwnerFields>(LISTS.owners, toFields(owner));
  return toOwner(created.id, created.fields);
}

export async function updateOwner(owner: Owner): Promise<void> {
  await updateListItem<OwnerFields>(LISTS.owners, owner.id, toFields(owner));
}

export async function deleteOwner(id: string): Promise<void> {
  await deleteListItem(LISTS.owners, id);
}

/**
 * Sets (ownerId) or clears (null) stores' owners in one batch — Admin →
 * Owners' store picker (owner ask 2026-10-29). Stores.OwnerId only.
 */
export async function setStoresOwner(changes: readonly { storeId: string; ownerId: string | null }[]): Promise<void> {
  if (changes.length === 0) return;
  const requests: ListOp[] = changes.map((c, i) => {
    const value = c.ownerId === null ? null : Number(c.ownerId);
    if (value !== null && !(Number.isInteger(value) && value > 0)) throw new Error('That owner couldn’t be found — refresh the page and try again.');
    return { id: `o-${i}`, method: 'PATCH' as const, list: LISTS.stores, itemId: c.storeId, fields: { OwnerId: value } };
  });
  const responses = await executeListOps(requests);
  const failed = [...responses.values()].filter((r) => r.status < 200 || r.status >= 300);
  if (failed.length > 0) throw new Error(`${failed.length} of ${changes.length} store updates failed — reload and retry.`);
}

function toFields(owner: Omit<Owner, 'id'>): OwnerFields {
  return { Title: owner.name.trim(), Active: owner.active, OwnerNotes: owner.notes.trim().slice(0, OWNER_NOTES_MAX) };
}

/** URL / select value for "stores with no owner set". */
export const NO_OWNER = 'none';

/**
 * The Owner filter (reports, Analytics, boards): '' = every store; NO_OWNER =
 * stores with no owner; an owner's id = that owner's stores. An unknown id
 * matches nothing (a stale link shows an empty page, never everything).
 */
export function storesOfOwner<S extends Pick<Store, 'ownerId'>>(stores: readonly S[], owner: string): S[] {
  const key = owner.trim();
  if (!key) return [...stores];
  if (key === NO_OWNER) return stores.filter((s) => s.ownerId === null);
  return stores.filter((s) => s.ownerId !== null && String(s.ownerId) === key);
}

/** The owner's name for a store, '' when none (or unknown). */
export function ownerNameOf(store: Pick<Store, 'ownerId'>, owners: ReadonlyMap<string, Owner>): string {
  return store.ownerId === null ? '' : (owners.get(String(store.ownerId))?.name ?? '');
}
